'use client'

import { type FilesRecord, type ValidExtensions } from '@/lib/utils/upload'
import { type PrimitiveAtom } from 'jotai'
import { useFormContext } from '../../Form.client'
import { AbstractInput } from '../../_shared/AbstractInput.client'
import { DropZone } from './DropZone.client'
import { getAttachedFiles } from './_hooks/useDropZoneFiles'
import { useDropZoneSharedLimit } from './_hooks/useDropZoneSharedLimit'

type DropZoneInputBaseProps = UiComponentProps<{
  isDisabled?: boolean
  isLoading?: boolean
  validExtensions: ValidExtensions[]
  allowsMultiple?: boolean
  name: string
}>

// Kept as a standalone union so both prop types below can intersect it: omitting a non
// discriminant key from the assembled type would flatten the xor instead of preserving it
type DropZoneSharedLimitProps =
  | {
      filesAtom?: PrimitiveAtom<FilesRecord>
      /** Size limit shared with every field bound to the same atom, in Mb */
      sharedLimit?: number
    }
  | {
      filesAtom?: never
      sharedLimit?: never
    }

type DropZoneInputProps = DropZoneInputBaseProps &
  DropZoneSharedLimitProps & {
    required?: boolean
  }

type DropZoneFieldProps = DropZoneInputBaseProps &
  DropZoneSharedLimitProps & {
    files: File[]
    onFilesChange: (files: File[]) => void
    isRequired: boolean
    errorMessage?: string
  }

// Split out of the render prop so the shared-limit hook gets a stable component to live in
function DropZoneField({
  name,
  files,
  onFilesChange,
  filesAtom,
  sharedLimit,
  errorMessage,
  ...props
}: DropZoneFieldProps) {
  const form = useFormContext()
  const { remainingSize } = useDropZoneSharedLimit({ files, filesAtom, sharedLimit, name })

  return (
    <DropZone
      {...props}
      files={files}
      onFilesChange={(nextFiles) => {
        form.clearErrors(name)
        onFilesChange(nextFiles)
      }}
      errorMessage={errorMessage}
      onValidationError={({ message }) => {
        form.setError(name, { message })
      }}
      remainingSize={remainingSize}
    />
  )
}

export function DropZoneInput({ name, required, ...props }: DropZoneInputProps) {
  return (
    <AbstractInput
      required={required}
      name={name}
      render={({ field, fieldState }) => (
        <DropZoneField
          {...props}
          name={name}
          files={getAttachedFiles(field.value)}
          onFilesChange={field.onChange}
          isRequired={fieldState.isRequired}
          errorMessage={fieldState.error?.message}
        />
      )}
    />
  )
}
